import {memo} from 'react';
import type {ChangeEvent} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {C21_PAGE, CChecklistRow} from '@/pages/components/audit-program/c-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';
import {indentPaddingLeft, resolveDisplayLevel} from '@/pages/components/audit-program/templates/rowLevel';

export interface C21PageContent {
    title: string;
    intros: readonly string[] | string[];
    rows: readonly CChecklistRow[] | CChecklistRow[];
}

export interface C21IndependenceViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
    /** Firm template from DB; falls back to code defaults. */
    content?: C21PageContent | null;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.75rem',
    py: 0.75,
    px: 0.5,
};
const bodySx = {
    ...cellBorder,
    py: 0.35,
    px: 0.5,
    verticalAlign: 'top' as const,
};

/** Category headings (e.g. "1 Fees") — no Yes/No. */
function isSectionHeading(row: CChecklistRow): boolean {
    if (row.kind === 'section' || row.kind === 'note') return true;
    if (row.kind !== 'main') return false;
    // Main rows that are short titles without a question mark
    return !row.text.includes('?');
}

function resolveAnswer(values: Record<string, string>, rowId: string): '' | 'Yes' | 'No' {
    const direct = values[`${rowId}.answer`];
    if (direct === 'Yes' || direct === 'No') return direct;
    // Legacy row-id migrations (pre-Excel-align ids)
    const legacyIdMap: Record<string, string> = {
        r113: 'r112',
        r119: 'r118',
        r122: 'r121',
        r125: 'r124',
        r147: 'r146',
        r149: 'r148',
        r152: 'r151',
        r158: 'r157',
        r162: 'r161',
        r165: 'r164',
        r170: 'r169',
        r173: 'r172',
        r178: 'r177',
        r181: 'r180',
        r185: 'r184',
        r188: 'r187',
        r193: 'r192',
        r195: 'r194',
        r198: 'r197',
        r203: 'r202',
        r206: 'r205',
        r210: 'r209',
        r219: 'r218',
    };
    const prevId = legacyIdMap[rowId];
    if (prevId) {
        const migrated = values[`${prevId}.answer`];
        if (migrated === 'Yes' || migrated === 'No') return migrated;
    }
    // Migrate old defaults that stored Excel "V" in comments (No column)
    const legacy = values[`${rowId}.comments`];
    if (legacy === 'V' || legacy === 'No') return 'No';
    if (legacy === 'Yes' || legacy === 'Y') return 'Yes';
    return '';
}

const AnswerRow = memo(function AnswerRow({
    row,
    answer,
    onChange,
}: {
    row: CChecklistRow;
    answer: '' | 'Yes' | 'No';
    onChange: (key: string, value: string) => void;
}) {
    const pl = indentPaddingLeft(resolveDisplayLevel(row));
    const indent = row.kind === 'sub';
    const setAnswer = (next: '' | 'Yes' | 'No') => {
        onChange(`${row.id}.answer`, next);
    };

    return (
        <TableRow>
            <TableCell sx={bodySx}>
                <Typography
                    variant="body2"
                    fontWeight={indent ? 400 : 600}
                    sx={{pl}}
                >
                    {row.num}
                </Typography>
            </TableCell>
            <TableCell sx={bodySx}>
                <Typography variant="body2" sx={{lineHeight: 1.4, pl, whiteSpace: 'pre-line'}}>
                    {row.text}
                </Typography>
            </TableCell>
            <TableCell sx={{...bodySx, textAlign: 'center'}}>
                <Box
                    component="input"
                    type="checkbox"
                    checked={answer === 'Yes'}
                    onChange={() => setAnswer(answer === 'Yes' ? '' : 'Yes')}
                    sx={{
                        width: 18,
                        height: 18,
                        cursor: 'pointer',
                        accentColor: (theme) => theme.palette.primary.main,
                    }}
                    aria-label={`${row.num || row.id} Yes`}
                />
            </TableCell>
            <TableCell sx={{...bodySx, textAlign: 'center'}}>
                <Box
                    component="input"
                    type="checkbox"
                    checked={answer === 'No'}
                    onChange={() => setAnswer(answer === 'No' ? '' : 'No')}
                    sx={{
                        width: 18,
                        height: 18,
                        cursor: 'pointer',
                        accentColor: (theme) => theme.palette.primary.main,
                    }}
                    aria-label={`${row.num || row.id} No`}
                />
            </TableCell>
        </TableRow>
    );
});

/** Excel C2.1 Independence Questionnaire — Yes / No checkboxes. */
export default function C21IndependenceView({
    header,
    values,
    onChange,
    content,
}: C21IndependenceViewProps) {
    const page = content ?? C21_PAGE;
    const intros = Array.isArray(page.intros) ? page.intros : [];
    const rows = Array.isArray(page.rows) ? page.rows : [];

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode="C2.1"
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1.5, mt: 0.5}}
            >
                {page.title || C21_PAGE.title}
            </Typography>

            {intros.map((intro, idx) => (
                <Typography
                    key={idx}
                    variant="body2"
                    sx={{mb: 1.25, lineHeight: 1.45, whiteSpace: 'pre-line', fontStyle: 'italic'}}
                >
                    {intro}
                </Typography>
            ))}

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 720, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{width: 56}}/>
                        <col/>
                        <col style={{width: 64}}/>
                        <col style={{width: 64}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} colSpan={2}/>
                            <TableCell
                                sx={{
                                    ...headSx,
                                    textAlign: 'center',
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                                }}
                            >
                                Yes
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...headSx,
                                    textAlign: 'center',
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                                }}
                            >
                                No
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => {
                            if (isSectionHeading(row)) {
                                const isLink = row.text.startsWith('http://') || row.text.startsWith('https://');
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={bodySx} colSpan={4}>
                                            {isLink ? (
                                                <Typography
                                                    component="a"
                                                    href={row.text}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    variant="body2"
                                                    sx={{
                                                        py: 0.25,
                                                        display: 'inline-block',
                                                        wordBreak: 'break-all',
                                                        color: 'primary.main',
                                                    }}
                                                >
                                                    {row.text}
                                                </Typography>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={row.kind === 'note' && row.num ? 400 : 700}
                                                    sx={{
                                                        py: 0.25,
                                                        fontStyle: row.kind === 'note' && !row.num ? 'italic' : 'normal',
                                                        color: row.kind === 'note' && !row.num ? 'text.secondary' : 'text.primary',
                                                        whiteSpace: 'pre-line',
                                                        pl: row.kind === 'note' && row.num ? 1 : 0,
                                                    }}
                                                >
                                                    {row.num ? `${row.num} ${row.text}` : row.text}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            }
                            if (row.signature) {
                                const nameKey = row.signature === 'engagement' ? 'engagementPartnerName' : 'secondPartnerName';
                                const dateKey = row.signature === 'engagement' ? 'engagementPartnerDate' : 'secondPartnerDate';
                                const partnerFallback =
                                    row.signature === 'engagement'
                                        ? (values.partnerName || header.partnerName || '')
                                        : '';
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={bodySx} colSpan={4}>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr 160px',
                                                    gap: 1,
                                                    alignItems: 'center',
                                                    maxWidth: 640,
                                                    py: 0.5,
                                                }}
                                            >
                                                <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                                                    <Typography variant="body2" sx={{minWidth: 200, flexShrink: 0}}>
                                                        {row.text}
                                                    </Typography>
                                                    <Box
                                                        component="input"
                                                        value={values[nameKey] || partnerFallback}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                            onChange(nameKey, e.target.value)
                                                        }
                                                        sx={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            m: 0,
                                                            border: '1px solid',
                                                            borderColor: 'rgba(0, 0, 0, 0.23)',
                                                            borderRadius: 1,
                                                            px: 1,
                                                            py: '4px',
                                                            fontSize: (theme) => theme.typography.body2.fontSize,
                                                            fontFamily: 'inherit',
                                                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                                                            outline: 'none',
                                                        }}
                                                    />
                                                </Box>
                                                <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                                                    <Typography variant="caption" color="text.secondary">Date</Typography>
                                                    <Box
                                                        component="input"
                                                        value={values[dateKey] ?? ''}
                                                        placeholder="dd/mm/yyyy"
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                            onChange(dateKey, e.target.value)
                                                        }
                                                        sx={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            m: 0,
                                                            border: '1px solid',
                                                            borderColor: 'rgba(0, 0, 0, 0.23)',
                                                            borderRadius: 1,
                                                            px: 1,
                                                            py: '4px',
                                                            fontSize: (theme) => theme.typography.body2.fontSize,
                                                            fontFamily: 'inherit',
                                                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                                                            outline: 'none',
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            }
                            if (row.fillIn) {
                                const pl = indentPaddingLeft(resolveDisplayLevel(row));
                                const fieldValue = values[`${row.id}.text`] ?? values[`${row.id}.comments`] ?? '';
                                if (row.fillInMultiline) {
                                    const shortBox = row.id === 'r240';
                                    return (
                                        <TableRow key={row.id}>
                                            <TableCell sx={bodySx} colSpan={4}>
                                                {row.text ? (
                                                    <Typography variant="body2" sx={{mb: 0.5, pl}}>{row.text}</Typography>
                                                ) : null}
                                                <Box
                                                    component="textarea"
                                                    value={fieldValue}
                                                    rows={shortBox ? 2 : 4}
                                                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                                                        onChange(`${row.id}.text`, e.target.value)
                                                    }
                                                    sx={{
                                                        display: 'block',
                                                        width: '100%',
                                                        boxSizing: 'border-box',
                                                        m: 0,
                                                        border: '1px solid',
                                                        borderColor: 'rgba(0, 0, 0, 0.23)',
                                                        borderRadius: 1,
                                                        px: 1,
                                                        py: 0.75,
                                                        fontSize: (theme) => theme.typography.body2.fontSize,
                                                        fontFamily: 'inherit',
                                                        lineHeight: 1.45,
                                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                                                        outline: 'none',
                                                        resize: 'vertical',
                                                        '&:focus': {
                                                            borderColor: 'primary.main',
                                                            boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                }
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={bodySx}>
                                            <Typography variant="body2" sx={{pl}}>{row.num}</Typography>
                                        </TableCell>
                                        <TableCell sx={bodySx} colSpan={3}>
                                            <Box sx={{display: 'flex', gap: 1, alignItems: 'center', pl}}>
                                                <Typography variant="body2" sx={{flexShrink: 0, lineHeight: 1.4}}>
                                                    {row.text}
                                                </Typography>
                                                <Box
                                                    component="input"
                                                    value={fieldValue}
                                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                        onChange(`${row.id}.text`, e.target.value)
                                                    }
                                                    sx={{
                                                        width: row.id === 'r216' ? 72 : 240,
                                                        maxWidth: row.id === 'r216' ? 96 : '40%',
                                                        minWidth: row.id === 'r216' ? 56 : 120,
                                                        m: 0,
                                                        border: '1px solid',
                                                        borderColor: 'rgba(0, 0, 0, 0.23)',
                                                        borderRadius: 1,
                                                        px: 1,
                                                        py: '4px',
                                                        fontSize: (theme) => theme.typography.body2.fontSize,
                                                        fontFamily: 'inherit',
                                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                                                        outline: 'none',
                                                        '&:focus': {
                                                            borderColor: 'primary.main',
                                                            boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            }
                            return (
                                <AnswerRow
                                    key={row.id}
                                    row={row}
                                    answer={resolveAnswer(values, row.id)}
                                    onChange={onChange}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
